import { CurrentUser } from '../general/modules/user/currentUser';
import { UserStatEntryType } from './UserStatEntryType';
import consoleLogger from '../logger/consoleLogger';
import { StatSendEventError } from './StatSendEventError';
import { getUserSuid } from '../general/modules/getUserSuid';
import { ServerTime } from '../utils/server-time';
import { getHubApi } from '../general/modules/accountServiceApi/getAccountServiceApi';

type StatEntryType = UserStatEntryType;

type StatRequest = {
    factTs: number,
    fact_name: StatEntryType,
    count: number,
    special_iprops32: number[],
    special_cprops: string[]
};

type SendStatEventRequest = { fact: StatRequest; suid: string; token: string };

export class Stat {
    private static isActive: boolean = false;
    private static queuedEvents: StatRequest[] = [];
    private static serverTime: ServerTime = new ServerTime();

    public static activate() {
        this.isActive = true;
        this.flushQueue();
    }

    public static async sendEvent(
        factName: StatEntryType,
        count: number = 1,
        special_iprops: number[] = [],
        special_cprops: string[] = [],
        inFactTs: number = null
    ): Promise<void> {
        if (CurrentUser.getInstance().isSimulate()) {
            return;
        }

        let factTs: number;

        if (!inFactTs) {
            factTs = await this.serverTime.getTime();
        } else {
            factTs = inFactTs;
        }

        const data: StatRequest = {
            count,
            factTs,
            fact_name: factName,
            special_cprops,
            special_iprops32: special_iprops
        };

        if (this.isActive === false) {
            this.queueEvent(data);
        }

        await this.sendViaHubApi(data, window.app?.config?.user_auth_token);
    }

    public static async flushQueue(): Promise<void> {
        consoleLogger.info('Launching events resend');

        const events = Stat.queuedEvents;
        Stat.queuedEvents = [];
        events.forEach((event) => {
            Stat.sendEvent(event.fact_name, event.count, event.special_iprops32, event.special_cprops, event.factTs);
        });
    }

    private static sendViaHubApi(data: StatRequest, token: string): void {
        const suid = getUserSuid();
        const requestData: SendStatEventRequest = {
            fact: data,
            suid: suid,
            token: token
        };
        const hubApi = getHubApi();

        hubApi.sendStatEvent(window.app.deviceIdentifier, requestData)
            .then((response) => {
                if ('error' in response || response?.sent !== true) {
                    Stat.queueEvent(data);
                    if ('error' in response) {
                        console.error(response.error);
                        window.Sentry.captureException(new StatSendEventError('Failed to send event, not success'));
                    }
                }
            })
            .catch((error) => {
                if (error?.response?.status === 403) {
                    return;
                }

                Stat.queueEvent(data);
                const statSendError = new StatSendEventError('Failed to send event', error);
                console.error(error, statSendError.stack);
                window.Sentry.captureException(statSendError);
            });
    }

    private static queueEvent(params: StatRequest): void {
        Stat.queuedEvents.push(params);
    }
}
